import { useState } from "react";
import { Star, ArrowUpRight, Quote } from "lucide-react";
import { REVIEWS } from "@/lib/site";

const INITIAL_VISIBLE = 6;

export default function Testimonials() {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? REVIEWS.items : REVIEWS.items.slice(0, INITIAL_VISIBLE);

  return (
    <section
      data-testid="testimonials-section"
      className="relative py-24 md:py-32 bg-sand"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
          <div>
            <p className="overline text-ink" data-testid="reviews-overline">
              Reviews from Google
            </p>
            <h2
              data-testid="reviews-heading"
              className="mt-5 font-display text-ink text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
            >
              In their own
              <br />
              <span className="italic text-slate2">words.</span>
            </h2>
          </div>

          <div className="md:text-right">
            <div className="flex items-center gap-3 md:justify-end">
              <Stars rating={REVIEWS.aggregate.rating} large />
              <span
                className="font-display text-ink text-3xl"
                data-testid="reviews-aggregate-rating"
              >
                {REVIEWS.aggregate.rating.toFixed(1)}
              </span>
            </div>
            <p className="text-muted2 text-sm mt-2">
              <span data-testid="reviews-aggregate-count">
                {REVIEWS.aggregate.count}
              </span>{" "}
              verified Google reviews
            </p>
          </div>
        </div>

        {/* Masonry grid via CSS columns — keeps natural card heights */}
        <div className="columns-1 md:columns-2 lg:columns-3 gap-6 [column-fill:_balance]">
          {visible.map((r, i) => (
            <ReviewCard
              key={`${r.author}-${r.time}-${(r.text || "").slice(0, 24)}`}
              review={r}
              index={i}
            />
          ))}
        </div>

        {REVIEWS.items.length > INITIAL_VISIBLE && (
          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-6">
            <button
              onClick={() => setShowAll((v) => !v)}
              data-testid="reviews-toggle-all"
              className="text-sm text-ink font-medium border-b border-ink pb-1 hover:text-brass hover:border-brass transition-colors"
            >
              {showAll
                ? "Show fewer reviews"
                : `Read all ${REVIEWS.items.length} reviews`}
            </button>
            <a
              href={REVIEWS.profileUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reviews-google-link"
              className="group inline-flex items-center gap-2 text-sm text-muted2 hover:text-ink transition-colors"
            >
              See on Google
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
        )}
      </div>
    </section>
  );
}

function ReviewCard({ review, index }) {
  return (
    <article
      data-testid={`review-card-${index}`}
      className="break-inside-avoid mb-6 bg-white border border-border2 rounded-2xl p-7 hover:border-brass/60 transition-colors duration-300 relative"
    >
      <Quote className="absolute top-5 right-5 w-5 h-5 text-brass/30" />
      <Stars rating={review.rating} />
      <p
        data-testid={`review-text-${index}`}
        className="mt-4 text-text2 text-[0.95rem] leading-relaxed"
      >
        “{review.text}”
      </p>
      <div className="mt-6 pt-5 border-t border-border2/70 flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-ink text-white inline-flex items-center justify-center font-display text-sm">
          {initials(review.author)}
        </div>
        <div className="min-w-0">
          <div className="text-ink font-medium text-sm" data-testid={`review-author-${index}`}>
            {review.author}
          </div>
          <div className="text-muted2 text-xs mt-0.5 flex items-center gap-2 flex-wrap">
            {review.badge && (
              <span className="inline-flex items-center gap-1 text-brass">
                <span className="w-1 h-1 rounded-full bg-brass" />
                {review.badge}
              </span>
            )}
            <span>{review.time}</span>
            <span className="text-muted2/60">·</span>
            <span className="inline-flex items-center gap-1">
              <GoogleGlyph />
              Google
            </span>
          </div>
        </div>
      </div>
    </article>
  );
}

function Stars({ rating, large = false }) {
  const size = large ? "w-5 h-5" : "w-4 h-4";
  return (
    <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`${size} ${
            i < Math.round(rating) ? "fill-brass text-brass" : "text-border2"
          }`}
        />
      ))}
    </div>
  );
}

function GoogleGlyph() {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 48 48"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        fill="#4285F4"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#34A853"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#EA4335"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

function initials(name) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || "")
    .join("");
}
